/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "./useAuth";
import usePluginUtils from "./usePluginUtils";
import { useGetMemberships } from "../lib/react-query/useGetMemberships";
import { useGetPurchasedMemberships } from "../lib/react-query/useGetPurchasedMemberships";
import { getServiceBookingUnits } from "../utils/service";

export const ACCESS_STATUS = {
  GRANTED: "granted",
  CHOOSE_MEMBERSHIP: "choose_membership",
  DENIED_LOGIN: "denied_login",
  DENIED_MEMBERSHIP: "denied_membership",
  MEMBERSHIP_EXHAUSTED: "membership_exhausted",
  LOADING: "loading",
};

/**
 * Tells if the current client can pick a members-only service.
 * Returns GRANTED when the membership plugin is off — the single off-switch
 * for the whole flow.
 *
 * Returns { status, requiredMemberships, clientMembership, usableMemberships }.
 * requiredMemberships is filled only on DENIED_MEMBERSHIP and lists memberships
 * that would unlock the service. clientMembership is the matching purchase on
 * GRANTED (single matching membership) — its id is sent with the booking and
 * name/rest/count feed the applied-membership summary.
 * usableMemberships is filled only on CHOOSE_MEMBERSHIP, when several of the
 * client's memberships cover the service and they must pick which one to spend.
 * exhaustedMemberships is filled only on MEMBERSHIP_EXHAUSTED.
 *
 * Capacity is cart-aware: remaining = server `rest` minus the cart's services
 * already assigned to that membership — stops over-drawing a shared membership.
 */
export const useServiceMembershipAccess = (service) => {
  const { isAuthenticated, token, isClientLoading } = useAuth();
  const { isPluginEnabled } = usePluginUtils();
  const selectedServices = useSelector((state) => state.booking.selectedServices);
  const membershipAccessGrants = useSelector((state) => state.booking.membershipAccessGrants);
  const membershipPluginEnabled = isPluginEnabled("membership");
  const requiredMembershipIds = useMemo(() => service?.memberships || [], [service?.memberships]);
  const requiresMembership = membershipPluginEnabled && requiredMembershipIds.length > 0;
  // Future-start memberships count too — a client may hold a valid membership
  // that hasn't started yet. The service stays selectable; first-available-day
  // (check_membership) shifts the calendar to the coverage start so the booking
  // lands on/after the membership start date.
  const usable = useGetPurchasedMemberships(
    { "filter[service_id]": service?.id, "filter[count]": 1 },
    { enabled: requiresMembership && isAuthenticated && !!service?.id }
  );

  // Shop list — only needed to tell the client which memberships would unlock
  // the service when access is denied.
  const memberships = useGetMemberships({}, { enabled: requiresMembership });

  // After sign-in token is set before client arrives, so isAuthenticated is
  // briefly false. Treat that window as LOADING — otherwise we'd flash
  // DENIED_LOGIN and re-open the sign-in modal for one frame.
  const isAuthInFlight = !!token && !isAuthenticated;

  return useMemo(() => {
    const empty = {
      requiredMemberships: [],
      clientMembership: null,
      usableMemberships: [],
      exhaustedMemberships: [],
    };
    if (!service) return { ...empty, status: ACCESS_STATUS.GRANTED };
    if (!requiresMembership) return { ...empty, status: ACCESS_STATUS.GRANTED };

    if (isAuthInFlight || isClientLoading) return { ...empty, status: ACCESS_STATUS.LOADING };
    if (!isAuthenticated) return { ...empty, status: ACCESS_STATUS.DENIED_LOGIN };
    if (usable.isLoading || usable.isFetching || memberships.isLoading) return { ...empty, status: ACCESS_STATUS.LOADING };

    const usableMemberships = usable.data?.data || [];

    // Units other selected services already claim on this membership.
    const inCartUsage = (membershipId) =>
      selectedServices.reduce((acc, s) => {
        const cmId = membershipAccessGrants[s.id]?.clientMembershipId;
        const claims = +s.id !== +service.id && cmId != null && +cmId === +membershipId;
        return claims ? acc + getServiceBookingUnits(s) : acc;
      }, 0);

    const requiredUnits = getServiceBookingUnits(service);

    // Memberships that can still cover this service's units after the cart.
    const withCapacity = usableMemberships.filter(
      (m) => m.membership?.is_unlimited || m.rest - inCartUsage(m.id) >= requiredUnits
    );

    // One with capacity — auto-pick it.
    if (withCapacity.length === 1) {
      return { ...empty, status: ACCESS_STATUS.GRANTED, clientMembership: withCapacity[0] };
    }

    // Several — let the client choose which to spend from.
    if (withCapacity.length > 1) {
      return { ...empty, status: ACCESS_STATUS.CHOOSE_MEMBERSHIP, usableMemberships: withCapacity };
    }

    // Owns covering memberships but the cart spent them all (vs owns none).
    if (usableMemberships.length > 0) {
      return { ...empty, status: ACCESS_STATUS.MEMBERSHIP_EXHAUSTED, exhaustedMemberships: usableMemberships };
    }

    const requiredSet = new Set(requiredMembershipIds.map(Number));
    const requiredMemberships = (memberships.data?.data || []).filter((m) => requiredSet.has(+m.id));

    return { ...empty, status: ACCESS_STATUS.DENIED_MEMBERSHIP, requiredMemberships };
  }, [
    service,
    requiresMembership,
    requiredMembershipIds,
    isAuthenticated,
    isAuthInFlight,
    isClientLoading,
    selectedServices,
    membershipAccessGrants,
    memberships.data,
    memberships.isLoading,
    usable.data,
    usable.isLoading,
    usable.isFetching,
  ]);
};
